import classNames from 'classnames'
import { Link } from 'gatsby'
import React, { useContext } from 'react'

import { PlainProduct } from '../../hooks/useProducts'
import { useSelectedCurrency } from '../../hooks/useSelectedCurrency'
import { formatPriceRange, LocaleContext } from '../../locale/i18n'
import { staticPagePaths } from '../../locale/pathing'
import { normalizeFormula } from '../../logic/formatting'
import { gaEvents } from '../../logic/ga4'
import { getPriceRange } from '../../logic/price'
import { ProductFiltersByType } from '../../logic/productFiltering'
import { standardToString } from '../../logic/standard'
import { StaticPage } from '../../logic/staticPage'
import { buttonClasses } from '../Button'
import { HighlightText } from '../HighlightText'

const ProductListItem = ({
  className,
  filters,
  product,
}: {
  className?: string
  filters?: ProductFiltersByType
  product: PlainProduct
}) => {
  const { locale, t } = useContext(LocaleContext)
  const { currency } = useSelectedCurrency()
  const { slug, standards, descriptionRaw } = product

  const name = normalizeFormula(product.name ?? '')
  const productUrl = [
    staticPagePaths[StaticPage.PRODUCTS][locale],
    slug,
    '/',
  ].join('')
  const priceRange = getPriceRange(product, currency)

  const standardList =
    standards.slice(0, 3).map(standardToString).join(', ') +
    (standards.length > 3 ? ', …' : '')

  return (
    <Link
      className={classNames(
        'ga-productPreviewButton flex flex-col rounded-lg border border-gray-300 bg-white px-4 py-3 wrap-anywhere text-text hover:border-primary focus-visible:border-primary',
        className,
      )}
      to={productUrl}
      onClick={() => gaEvents.productClicked(product)}
    >
      <div className="flex flex-1 flex-col gap-2 text-sm">
        <h4 className="color-heading font-primary leading-normal font-semibold wrap-anywhere">
          <HighlightText highlight={filters?.text.value.split(' ')}>
            {name}
          </HighlightText>
        </h4>
        {standardList && (
          <div className="text-gray-500">
            <HighlightText highlight={filters?.text.value.split(' ')}>
              {standardList}
            </HighlightText>
          </div>
        )}
        {descriptionRaw && (
          <div className="line-clamp-6 leading-snug text-ellipsis text-gray-500">
            <HighlightText highlight={filters?.text.value.split(' ')}>
              {descriptionRaw}
            </HighlightText>
          </div>
        )}
        <div />
      </div>
      <div className="text-base">
        <div className="flex items-center gap-3">
          {priceRange && (
            <div className="text-lg font-semibold whitespace-nowrap">
              {formatPriceRange(priceRange, locale, currency)}
            </div>
          )}
          <div className="spacer" />
          <span className={buttonClasses({ size: 'base', variant: 'outline' })}>
            {t('productListing.readMore')}
          </span>
        </div>
      </div>
    </Link>
  )
}
export default ProductListItem
